import React, { useEffect, useState } from "react";
import { Plus, Trash2, Receipt, CreditCard, Eye, Pencil, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, openPdf, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const CATEGORIES = [
  "Consultation",
  "Procedure",
  "Lab",
  "Radiology",
  "Medicines",
  "Admission",
  "Other",
];

const PAYMENT_MODES = ["Cash", "UPI", "Card"];

const EMPTY_ITEM = {
  description: "",
  category: "Consultation",
  qty: 1,
  rate: 0,
};

export default function Billing() {
  const { fq, facilities, facilityId } = useApp();

  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);

  // Create bill
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [form, setForm] = useState({
    patient_id: "",
    discount: 0,
    tax: 0,
    paid: 0,
    payment_mode: "Cash",
  });

  // Bill details
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Payment
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    mode: "Cash",
    reference: "",
    notes: "",
  });
  const [savingPayment, setSavingPayment] = useState(false);

  const load = () =>
    api
      .get(`/bills${fq ? `?${fq}` : ""}`)
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(errMsg(e)));

const handleDeleteBill = async (bill) => {
  const confirmed = window.confirm(
    `Move invoice ${bill.number} to Recycle Bin?`
  );

  if (!confirmed) return;

  try {
    await api.delete(`/bills/${bill.id}`);

    toast.success("Bill moved to Recycle Bin.");

    await load();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [fq]);

  useEffect(() => {
    api
      .get("/patients")
      .then(({ data }) => setPatients(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const subtotal = items.reduce(
    (s, i) => s + Number(i.qty || 0) * Number(i.rate || 0),
    0
  );

  const total = Math.max(
    0,
    subtotal - Number(form.discount || 0) + Number(form.tax || 0)
  );

  // -----------------------------
  // CREATE BILL
  // -----------------------------
  const save = async () => {
    const target =
      facilityId === "all" ? facilities[0]?.id : Number(facilityId);

    if (!form.patient_id) {
      return toast.error("Select a patient");
    }

    if (!target) {
      return toast.error("Select a facility");
    }

    try {
      const { data } = await api.post("/bills", {
        patient_id: Number(form.patient_id),
        facility_id: target,
        items: items
          .filter((i) => i.description)
          .map((i) => ({
            ...i,
            qty: Number(i.qty),
            rate: Number(i.rate),
          })),
        discount: Number(form.discount) || 0,
        tax: Number(form.tax) || 0,
        paid: Number(form.paid) || 0,
        payment_mode: form.payment_mode,
      });

      toast.success(`Bill ${data.number} created`);

      setOpen(false);

      setItems([{ ...EMPTY_ITEM }]);

      setForm({
        patient_id: "",
        discount: 0,
        tax: 0,
        paid: 0,
        payment_mode: "Cash",
      });

      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // -----------------------------
  // OPEN BILL DETAILS
  // -----------------------------
  const openDetails = async (bill) => {
    setDetailsOpen(true);
    setSelectedBill(bill);
    setLoadingDetails(true);

    try {
      const { data } = await api.get(`/bills/${bill.id}`);
      setSelectedBill(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoadingDetails(false);
    }
  };

  // -----------------------------
  // OPEN PAYMENT WINDOW
  // -----------------------------
  const openPayment = (bill = selectedBill) => {
  if (!bill) return;

  const totalAmount = Number(bill.total || 0);
  const paidAmount = Number(bill.paid || 0);
  const outstanding = Math.max(0, totalAmount - paidAmount);

  if (outstanding <= 0) {
    toast.info("This bill is already fully paid.");
    return;
  }

  setSelectedBill(bill);

  setPaymentForm({
    amount: outstanding.toFixed(2),
    mode: "Cash",
    reference: "",
    notes: "",
  });

  setPaymentOpen(true);
};
  // -----------------------------
  // RECORD PAYMENT
  // -----------------------------
  const recordPayment = async () => {
    if (!selectedBill) return;

    const amount = Number(paymentForm.amount || 0);
    const totalAmount = Number(selectedBill.total || 0);
    const paidAmount = Number(selectedBill.paid || 0);
    const outstanding = Math.max(0, totalAmount - paidAmount);

    if (amount <= 0) {
      return toast.error("Enter a valid payment amount.");
    }

    if (amount > outstanding) {
      return toast.error(
        `Payment cannot exceed outstanding amount of ₹${outstanding.toFixed(2)}.`
      );
    }

    setSavingPayment(true);

    try {
      await api.post(`/bills/${selectedBill.id}/payments`, {
        amount,
        payment_mode: paymentForm.mode,
        reference_number: paymentForm.reference || "",
        notes: paymentForm.notes || "",
      });

      toast.success("Payment recorded successfully.");

      setPaymentOpen(false);

      // Refresh bill details
      const { data } = await api.get(`/bills/${selectedBill.id}`);
      setSelectedBill(data);

      // Refresh billing table
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingPayment(false);
    }
  };

  // -----------------------------
  // STATUS BADGE
  // -----------------------------
 const statusBadge = (bill) => {
  const total = Number(bill?.total || 0);
  const paid = Number(bill?.paid || 0);
  const outstanding = Math.max(0, total - paid);

  if (outstanding <= 0) {
    return <Badge variant="secondary">Paid</Badge>;
  }

  if (paid > 0) {
    return <Badge variant="outline">Partial</Badge>;
  }

  return <Badge variant="destructive">Pending</Badge>;
};

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div className="space-y-5" data-testid="billing-page">

      {/* HEADER */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">
            Revenue desk
          </p>

          <h1 className="font-heading text-3xl font-bold tracking-tight">
            Billing
          </h1>

          <p className="text-sm text-muted-foreground mt-1">
            Create bills, collect payments and track outstanding balances.
          </p>
        </div>

        <Button
          className="gap-2"
          onClick={() => setOpen(true)}
          data-testid="create-bill-button"
        >
          <Plus className="h-4 w-4" />
          New bill
        </Button>
      </div>

      {/* BILL TABLE */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="bills-table">

            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Due</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>

              {rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-10 text-sm text-muted-foreground"
                  >
                    No bills raised yet.
                  </TableCell>
                </TableRow>
              )}

              {rows.map((b) => {
                const totalAmount = Number(b.total || 0);
                const paidAmount = Number(b.paid || 0);
                const dueAmount = Math.max(
                  0,
                  totalAmount - paidAmount
                );

                return (
                  <TableRow
                    key={b.id}
                    data-testid={`bill-row-${b.id}`}
                    className="hover:bg-muted/50"
                  >

                    <TableCell className="font-mono text-xs">
                      {b.number}
                    </TableCell>

                    <TableCell className="font-medium">
                      {b.patient_name}
                    </TableCell>

                    <TableCell>
                      {(b.items || []).length}
                    </TableCell>

                    <TableCell className="text-right tabular-nums">
                      ₹{totalAmount.toFixed(2)}
                    </TableCell>

                    <TableCell className="text-right tabular-nums">
                      ₹{paidAmount.toFixed(2)}
                    </TableCell>

                    <TableCell className="text-right tabular-nums font-medium">
                      ₹{dueAmount.toFixed(2)}
                    </TableCell>

                    <TableCell>
                      {b.payment_mode || "-"}
                    </TableCell>

                    <TableCell>
                      {statusBadge(b)}
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">

                        <Button
                          size="sm"
                          variant="ghost"
                          title="View bill"
                          onClick={() => openDetails(b)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          title="Record Payment"
                          onClick={() => {
                            openPayment(b);
                        }}
                    > 
                       <CreditCard className="h-3.5 w-3.5" />
                        Payment
                      </Button>
                       
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          title="Receipt PDF"
                          data-testid={`bill-pdf-${b.id}`}
                          onClick={() =>
                            openPdf(`/bills/${b.id}/pdf`)
                          }
                        >
                          <Receipt className="h-3.5 w-3.5" />
                          PDF
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 text-destructive hover:text-destructive"
                          title="Move to Recycle Bin"
                          onClick={() => handleDeleteBill(b)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </Button>

                      </div>
                    </TableCell>

                  </TableRow>
                );
              })}

            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ===================================================== */}
      {/* CREATE BILL DIALOG */}
      {/* ===================================================== */}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="bill-dialog">

          <DialogHeader>
            <DialogTitle className="font-heading">
              Create bill
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">

            {/* PATIENT */}
            <div className="space-y-1.5">
              <Label className="text-xs">
                Patient
              </Label>

              <Select
                value={form.patient_id}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    patient_id: v,
                  })
                }
              >
                <SelectTrigger data-testid="bill-patient-select">
                  <SelectValue placeholder="Select patient" />
                </SelectTrigger>

                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={String(p.id)}
                    >
                      {p.name} · {p.uhid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* LINE ITEMS */}
            {items.map((it, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 items-end"
                data-testid={`bill-item-${i}`}
              >

                <div className="col-span-5 space-y-1.5">
                  <Label className="text-xs">
                    Description
                  </Label>

                  <Input
                    value={it.description}
                    data-testid={`bill-item-desc-${i}`}
                    onChange={(e) =>
                      setItems(
                        items.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                description: e.target.value,
                              }
                            : x
                        )
                      )
                    }
                  />
                </div>

                <div className="col-span-3 space-y-1.5">
                  <Label className="text-xs">
                    Category
                  </Label>

                  <Select
                    value={it.category}
                    onValueChange={(v) =>
                      setItems(
                        items.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                category: v,
                              }
                            : x
                        )
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-1 space-y-1.5">
                  <Label className="text-xs">
                    Qty
                  </Label>

                  <Input
                    type="number"
                    min="1"
                    value={it.qty}
                    onChange={(e) =>
                      setItems(
                        items.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                qty: e.target.value,
                              }
                            : x
                        )
                      )
                    }
                  />
                </div>

                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs">
                    Rate
                  </Label>

                  <Input
                    type="number"
                    min="0"
                    value={it.rate}
                    data-testid={`bill-item-rate-${i}`}
                    onChange={(e) =>
                      setItems(
                        items.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                rate: e.target.value,
                              }
                            : x
                        )
                      )
                    }
                  />
                </div>

                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    setItems(
                      items.filter((_, j) => j !== i)
                    )
                  }
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>

              </div>
            ))}

            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-testid="add-bill-item-button"
              onClick={() =>
                setItems([
                  ...items,
                  { ...EMPTY_ITEM },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add line item
            </Button>

            {/* DISCOUNT / TAX / PAID / MODE */}
            <div className="grid grid-cols-4 gap-3">

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Discount
                </Label>

                <Input
                  type="number"
                  min="0"
                  value={form.discount}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      discount: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Tax
                </Label>

                <Input
                  type="number"
                  min="0"
                  value={form.tax}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tax: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Initial payment
                </Label>

                <Input
                  type="number"
                  min="0"
                  value={form.paid}
                  data-testid="bill-paid-input"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      paid: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Mode
                </Label>

                <Select
                  value={form.payment_mode}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      payment_mode: v,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {PAYMENT_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>

            {/* TOTALS */}
            <div
              className="flex justify-end gap-6 text-sm font-mono"
              data-testid="bill-totals"
            >
              <span>
                Subtotal ₹{subtotal.toFixed(2)}
              </span>

              <span>
                Discount ₹{Number(form.discount || 0).toFixed(2)}
              </span>

              <span>
                Tax ₹{Number(form.tax || 0).toFixed(2)}
              </span>

              <span className="font-semibold">
                Total ₹{total.toFixed(2)}
              </span>
            </div>

          </div>

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={save}
              data-testid="save-bill-button"
            >
              Save bill
            </Button>

          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* ===================================================== */}
      {/* BILL DETAILS */}
      {/* ===================================================== */}

      <Dialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      >
        <DialogContent className="max-w-3xl">

          <DialogHeader>
            <DialogTitle>
              Bill Details
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="py-10 text-center text-muted-foreground">
              Loading bill...
            </div>
          ) : selectedBill ? (
            <div className="space-y-5">

              {/* SUMMARY */}
              <div className="grid grid-cols-4 gap-3">

                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">
                      Invoice
                    </p>
                    <p className="font-mono font-semibold">
                      {selectedBill.number}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">
                      Total
                    </p>
                    <p className="font-semibold">
                      ₹{Number(selectedBill.total || 0).toFixed(2)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">
                      Paid
                    </p>
                    <p className="font-semibold">
                      ₹{Number(selectedBill.paid || 0).toFixed(2)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">
                      Outstanding
                    </p>
                    <p className="font-semibold text-destructive">
                      ₹
                      {Math.max(
                        0,
                        Number(selectedBill.total || 0) -
                          Number(selectedBill.paid || 0)
                      ).toFixed(2)}
                    </p>
                  </CardContent>
                </Card>

              </div>

              {/* PATIENT */}
              <div>
                <p className="text-xs text-muted-foreground">
                  Patient
                </p>

                <p className="font-semibold">
                  {selectedBill.patient_name ||
                    selectedBill.patient?.name ||
                    "-"}
                </p>
              </div>

              {/* STATUS */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  Payment status:
                </span>

                {statusBadge(selectedBill)}
              </div>

              {/* ITEMS */}
              <div>
                <h3 className="font-semibold mb-2">
                  Bill items
                </h3>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">
                        Qty
                      </TableHead>
                      <TableHead className="text-right">
                        Rate
                      </TableHead>
                      <TableHead className="text-right">
                        Amount
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {(selectedBill.items || []).map(
                      (item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {item.description}
                          </TableCell>

                          <TableCell>
                            {item.category}
                          </TableCell>

                          <TableCell className="text-right">
                            {item.qty}
                          </TableCell>

                          <TableCell className="text-right">
                            ₹
                            {Number(
                              item.rate || 0
                            ).toFixed(2)}
                          </TableCell>

                          <TableCell className="text-right">
                            ₹
                            {(
                              Number(item.qty || 0) *
                              Number(item.rate || 0)
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* PAYMENT HISTORY */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">
                    Payment history
                  </h3>

                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={openPayment}
                  >
                    <CreditCard className="h-4 w-4" />
                    Record payment
                  </Button>
                </div>

                {(selectedBill.payments || []).length === 0 ? (
                  <div className="border rounded-lg p-5 text-center text-sm text-muted-foreground">
                    No payments recorded yet.
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y">

                    {selectedBill.payments.map(
                      (payment, index) => (
                        <div
                          key={
                            payment.id || index
                          }
                          className="p-3 flex items-center justify-between"
                        >

                          <div>
                            <p className="font-semibold">
                              ₹
                              {Number(
                                payment.amount || 0
                              ).toFixed(2)}
                            </p>

                            <p className="text-xs text-muted-foreground">
                              {payment.mode ||
                                payment.payment_mode ||
                                "Cash"}

                              {payment.reference
                                ? ` · ${payment.reference}`
                                : ""}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {payment.created_at
                                ? new Date(
                                    payment.created_at
                                  ).toLocaleString()
                                : ""}
                            </p>

                            {payment.notes && (
                              <p className="text-xs">
                                {payment.notes}
                              </p>
                            )}
                          </div>

                        </div>
                      )
                    )}

                  </div>
                )}
              </div>

              <DialogFooter>

                <Button
                  variant="outline"
                  onClick={() =>
                    openPdf(
                      `/bills/${selectedBill.id}/pdf`
                    )
                  }
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  PDF Receipt
                </Button>

                <Button
                  onClick={openPayment}
                  disabled={
                    Number(selectedBill.total || 0) <=
                    Number(selectedBill.paid || 0)
                  }
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment
                </Button>

              </DialogFooter>

            </div>
          ) : null}

        </DialogContent>
      </Dialog>

      {/* ===================================================== */}
      {/* RECORD PAYMENT */}
      {/* ===================================================== */}

      <Dialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
      >
        <DialogContent className="max-w-md">

          <DialogHeader>
            <DialogTitle>
              Record Payment
            </DialogTitle>
          </DialogHeader>

          {selectedBill && (
            <div className="space-y-4">

              <div className="rounded-lg border p-4 space-y-2">

                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Invoice
                  </span>

                  <span className="font-mono">
                    {selectedBill.number}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Total
                  </span>

                  <span>
                    ₹
                    {Number(
                      selectedBill.total || 0
                    ).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Already paid
                  </span>

                  <span>
                    ₹
                    {Number(
                      selectedBill.paid || 0
                    ).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between font-semibold">
                  <span>
                    Outstanding
                  </span>

                  <span className="text-destructive">
                    ₹
                    {Math.max(
                      0,
                      Number(selectedBill.total || 0) -
                        Number(selectedBill.paid || 0)
                    ).toFixed(2)}
                  </span>
                </div>

              </div>

              {/* AMOUNT */}
              <div className="space-y-1.5">
                <Label>
                  Payment amount
                </Label>

                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      amount: e.target.value,
                    })
                  }
                  autoFocus
                />
              </div>

              {/* MODE */}
              <div className="space-y-1.5">
                <Label>
                  Payment mode
                </Label>

                <Select
                  value={paymentForm.mode}
                  onValueChange={(v) =>
                    setPaymentForm({
                      ...paymentForm,
                      mode: v,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {PAYMENT_MODES.map(
                      (mode) => (
                        <SelectItem
                          key={mode}
                          value={mode}
                        >
                          {mode}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* REFERENCE */}
              <div className="space-y-1.5">
                <Label>
                  Transaction / Reference number
                </Label>

                <Input
                  placeholder={
                    paymentForm.mode === "UPI"
                      ? "UPI transaction ID"
                      : "Optional"
                  }
                  value={paymentForm.reference}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      reference:
                        e.target.value,
                    })
                  }
                />
              </div>

              {/* NOTES */}
              <div className="space-y-1.5">
                <Label>
                  Notes
                </Label>

                <Input
                  placeholder="Optional payment note"
                  value={paymentForm.notes}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      notes: e.target.value,
                    })
                  }
                />
              </div>

            </div>
          )}

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() =>
                setPaymentOpen(false)
              }
            >
              Cancel
            </Button>

            <Button
              onClick={recordPayment}
              disabled={savingPayment}
            >
              {savingPayment
                ? "Saving..."
                : "Record payment"}
            </Button>

          </DialogFooter>

        </DialogContent>
      </Dialog>

    </div>
  );
}